import { createContext, useContext, useEffect, useState, type DependencyList, type ReactNode } from 'react';
import { podeCadastrar, type FonteDeDados } from './fonte.js';

const Contexto = createContext<FonteDeDados | null>(null);

export function FonteProvider({ fonte, children }: { fonte: FonteDeDados; children: ReactNode }) {
  return <Contexto.Provider value={fonte}>{children}</Contexto.Provider>;
}

export function useFonte(): FonteDeDados {
  const f = useContext(Contexto);
  if (!f) throw new Error('useFonte fora do FonteProvider');
  return f;
}

export type Consulta<T> =
  | { estado: 'carregando' }
  | { estado: 'erro'; erro: Error }
  | { estado: 'pronto'; dado: T };

/**
 * Roda uma consulta na fonte e acompanha carregando/erro. Refaz quando `deps`
 * muda — e, na fonte local, sempre que o dado muda (salvar um cadastro ou
 * receber uma sincronizacao atualiza a tela sozinha).
 */
export function useConsulta<T>(fn: (f: FonteDeDados) => Promise<T>, deps: DependencyList): Consulta<T> {
  const fonte = useFonte();
  const [c, setC] = useState<Consulta<T>>({ estado: 'carregando' });

  useEffect(() => {
    let vivo = true;
    setC({ estado: 'carregando' });
    if (fonte.observar) {
      const inscricao = fonte.observar(() => fn(fonte)).subscribe({
        next: (dado) => vivo && setC({ estado: 'pronto', dado }),
        error: (e: unknown) => vivo && setC({ estado: 'erro', erro: e instanceof Error ? e : new Error(String(e)) }),
      });
      return () => {
        vivo = false;
        inscricao.unsubscribe();
      };
    }
    fn(fonte).then(
      (dado) => vivo && setC({ estado: 'pronto', dado }),
      (e: unknown) => vivo && setC({ estado: 'erro', erro: e instanceof Error ? e : new Error(String(e)) }),
    );
    return () => {
      vivo = false;
    };
  }, [fonte, ...deps]);

  return c;
}

/** A equipe pode cadastrar nesta fonte? (fonte local + papel admin/avaliador) */
export function usePodeCadastrar(): boolean {
  const fonte = useFonte();
  const u = useConsulta((f) => f.usuarioAtual(), []);
  return !!fonte.cadastro && u.estado === 'pronto' && podeCadastrar(u.dado);
}
